import { VideoStatus } from '@/pages/Groups/types'
import { IPagination } from '../types'

export const enum GroupType {
  Forming = 1,
  Active,
  Stopped,
  Ended,
  Reserver,
  HasDebt,
}

export interface IGroupParams {
  status?: GroupType
  academics?: boolean
}

export interface IGroupsData {
  groups: IGroupData[]
  total: number
}

export interface StaffGroupDetail {
  title: string
  staff: StaffOfGroup[]
}

export enum GroupStatus {
  // NOT_STARTED = 1,
  ACTIVE = 2,
  // ENDED = 3,
}
export interface Category {
  id: number
  hName: string
  displayName: string
  createdAt: Date
}
export interface Role {
  id: number
  name: string
  type: Role
  createdAt: Date
}

export interface Group {
  id: number
  name: string
  staffDetail: StaffGroupDetail[]
  studentsCount: number
  startedDate: Date
  endedDate: Date
  status: GroupStatus
  beginTime: string
  endTime: string
  category?: Category
  scheduleItems: ScheduleItem[]
}

export interface StaffOfGroup {
  id: number
  firstName: string
  lastName: string
  role: string
  hasPermission?: boolean
}

export interface ScheduleItem {
  id: number
  staff: StaffOfGroup
  daysOfWeek: string[]
  role: Role
  beginTime: string
  endTime: string
  beginDate: string
  endDate: string
  weekdays: number
  academics: StaffOfGroup[]
  isActive: boolean
}

export interface ISchedules {
  activeScheduleItems: ScheduleItem[]
  othersScheduleItems: ScheduleItem[]
}

export interface IGroupData {
  group: IGroup
  id: number
  hasPermission: boolean
  name?: string
  staff: IGroupStaffs
  scheduleItems: ScheduleItem[]
}

export interface IGroup {
  beginTime: string
  categoryName: string
  activeScheduleItems?: ScheduleItem[]
  category?: Category
  isStudying?: boolean
  endedDate: string
  endTime: string
  id: number
  name: string
  startedDate: string
  status: GroupType
  studentsCount: number
  staffDetail: IGroupStaffDetail[]
}

export interface IGroupStaffDetail {
  title: string
  staff: IGroupStaffs[]
}

export interface IGroupStaffs {
  firstName: string
  lastName: string
  photo: string
  id: number
  middleName?: string
}

export interface IGroupInfoParams extends IPagination {
  id: number
  staffId: number
}

export interface IGroupInfoData {
  groupData: IGroupInfo
  total: number
}
export interface IGroupInfo {
  group: IGroup
  id: number
  lessons: IGroupLesson[]
}

export interface IGroupLesson {
  createdAt: string
  hasRating: boolean
  homework: IGroupLessonHomework
  id: number
  name: string
  videos: IGroupLessonVideo[]
}

export interface IGroupLessonVideo {
  id: number
}

export interface IGroupLessonHomework {
  createdAt: string
  deadline: string
  homeworkInfo: IGroupLessonInfo
  id: number
  studentsCount: number
}

export interface IGroupLessonInfo {
  attachments: IGroupLessonVideoAttachment[]
  desc: string
  createdAt: string
  deadlinePassed: boolean
  deadline: string
  id: number
  comment?: string
  announcedAt?: string
  message?: string
  availableForSubmit: boolean
  allowedResubmission: boolean
  canSubmitHomework?: boolean
  canSubmit?: boolean
  homeworkDbs: IGroupLessonInfo[]
}

export interface IGroupLessonVideoAttachment {
  id?: number
  origName: string
  size: number
  url: string
  file?: File
  progress?: number
  isUploading?: boolean
  duration?: number
}

export type GroupLessonInfo = {
  id: number
  name: string
  date: string
  isRated: boolean
  hasRated: boolean
  dayType: string
  videosCount: number
  deadline: string
  status: number
  examId: number
  homeworkId: number
}

export interface IGroupLessonVideos {
  lessons: GroupLessonInfo[]
  total: number
}

export interface IGroupVideoLessons {
  group: IGroup
  id: number
  lessons: IGroupVideoLesson[]
}

export interface IGroupVideoLesson {
  createdAt: string
  hasRating: boolean
  homework: IGroupLessonHomework | null
  deadlinePassed: boolean
  attachments: IGroupLessonVideoAttachment[]
  id: number
  name: string
  videos: ILessonVideos[]
  examId?: number
  availableForSubmit?: boolean
  hasRated: boolean
  description?: string
}

export interface ILessonVideos {
  createdAt: string
  id: number
  index: number
  lesson: string
  origName: string
  size: number
  status: VideoStatus
  statusHistories: []
  url: string
  isLastVideo: boolean
}

export interface IGroupRatingParams {
  lessonId: number
  rating: number
  comment?: string
}

export interface ISuccessResponse {
  success: boolean
}

export interface IHomeworkChatsGet {
  homeworkChats: IHomeworkChatCome[]
  homeworkInfo: IGroupLessonInfo | null
  lesson?: IGroupLesson
  // homeworkChatsData: IHomeworkChat
  total: number
}

export interface IHomeworkChat {
  homeworkChats: IHomeworkChatCome[]
  homeworkInfo: IGroupLessonInfo | null
  lesson?: IGroupLesson
}

export interface IUploadHomewok {
  fileName: string
  path: string
  size: number
  progress?: number
}

export type THomeworkAttachments = {
  url: string
  origName: string
  size: number
}

export interface IHomeworkSend {
  attachments?: THomeworkAttachments[]
  homeworkId?: number
  message?: string
}

export type IHomeworkData = Omit<IHomeworkSend, 'homeworkId'>

export interface IExamInfo {
  id: number
  data: IHomeworkData
}

export type HomeworkSubmission = {
  attachments: IGroupLessonVideoAttachment[]
  message: string
  homeworkDbId: number
  id: number
}

export type HomeworkDbAttachments = Omit<IGroupLessonVideoAttachment, 'file' | 'progress' | 'isUploading'> & {
  key: string
}

export type HomeworkDbSubmission = {
  homeworkDbId: number
  message: string
  attachments: HomeworkDbAttachments[]
}

export type HomeworkDbRequestParams = {
  homeworkId: number
  homeworkChatId: number
  submissions: { homeworkDbId: number; message: string; attachments: HomeworkDbAttachments[] }[]
}

export interface IHomeworkChatCome {
  attachments: IGroupLessonVideoAttachment[]
  createdAt: string
  id: number
  message: string
  isEditable: boolean
  receiver: string
  sender: IHomeworkSender
  comment: string
  status: HomeworkChatsStatus
  updatedAt: string
  score?: number
  submissions?: HomeworkSubmission[]
}

export interface IHomeworkSender {
  firstName: string
  id: number
  lastName: string
  userType: UserTypes
  middleName?: string
  studentId: number
}

export enum HomeworkChatsStatus {
  WAITING = 1,
  REJECTED,
  ACCEPTED,
  CHECKING,
  NOT_DONE,
  NOT_GIVEN,
}

export enum UserTypes {
  STAFF = 1,
  STUDENT = 2,
}

export interface GetHomeworkParams {
  groupId?: number
  homeworkId: number
}

export type AddHomeworkParams = {
  homeworkId: number
  message?: string
  attachmentIds?: number[]
}

export type HomeworkDBRequest = {
  homeworkId: number
  homeworkChatId: number
  attachments: HomeworkDbSubmission[]
}

export interface IGroupLessonParams {
  groupId: number
  staffId: number
}

export enum IGroupChapterDeadline {
  Asc = 'ASC',
  Desc = 'DESC',
}

export interface IGetGroupChapters {
  id: number
  page?: number
  perPage?: number
  homeworkStatus?: HomeworkFilterStatus | string | null
  homeworkDeadline?: IGroupChapterDeadline
  createdAt?: IGroupChapterDeadline
}

export enum HomeworkFilterStatus {
  Waiting = 1,
  Rejected,
  Accepted,
  Checking,
  Uncompleted,
  NotGiven,
}

export const enum LessonDayType {
  Lesson = 'lesson',
  ChargeableAbsence = 'chargeable_absence',
}

export type Chapters = {
  id: number
  name: string
  videosCount: number
  homeworkStatus: HomeworkFilterStatus
  status: HomeworkFilterStatus
  createdAt: string
  deadline: string
  date: string
  xp: number
  coin: number
  score: number
  homeworkId: number
  homeworkSubmittedAt: string
  hasAttachments: boolean
  homeworkDeadline: string
  examId?: number
  hasRated: boolean
}

export interface IGroupChapters {
  groupData: {
    groupLessons: Chapters[]
    groupName: string
  }
  total: number
}

export type FileInformation = {
  file: File | null
  id: number
  hasError?: boolean
  progress?: number
  isUploading?: boolean
  origName: string
  size?: number
  url: string
  homeworkId?: number
  onAbort?: () => void
}

export interface IGroupLessonVideoNameResponse {
  id: number
  origName: string
}

export type IFile = Omit<FileInformation, 'url'>

export type TGetHomeworkFileUrlRes = {
  filename: string
}

export enum IExamStatus {
  Failed = 1,
  Passed,
  Absent,
  FailedExam,
}

export type IExamResult = {
  id: number
  firstName: string
  lastName: string
  middleName: string
  score: number
  status: IExamStatus
  failingReason: string
}

export type LastVideoParamsType = {
  videoId: number
  groupId: number
}

export type MakeHomeworkRes = {
  id: number
}
